import NiceModal from "@ebay/nice-modal-react";
import { Col, Row, Space, TableColumnsType, Tag, Typography } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { useRecoilValue } from "recoil";
import {
  AddButton,
  AffiliateCampaignCreateModal,
  AffiliateCampaignDetailModal,
  AffiliateCampaignEditModal,
  CampaignDashboard,
  EditButton,
  Table,
  ViewMoreButton,
} from "src/components";
import {
  CampaignDetail,
  CampaignStatus,
  CampaignType,
  useListAffiliateCampaigns,
} from "src/hooks/affiliate";
import { authInfoState } from "src/recoils";
import { Scope } from "src/types";

const { Title } = Typography;

const formatDateTime = (value?: string) =>
  value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-";

const statusColor: Record<string, string> = {
  [CampaignStatus.DRAFT]: "default",
  [CampaignStatus.ACTIVE]: "green",
  [CampaignStatus.INACTIVE]: "red",
  [CampaignStatus.COMPLETED]: "blue",
};

const typeColor: Record<string, string> = {
  [CampaignType.COMMISSION]: "geekblue",
  [CampaignType.TURNOVER]: "purple",
};

const AffiliateCampaign: React.FunctionComponent = () => {
  const { scope } = useRecoilValue(authInfoState);
  const canCreateCampaign = scope[Scope.AffiliateCampaignCreateButton]?.enable;
  const canEditCampaign = scope[Scope.AffiliateCampaignEditButton]?.enable;
  const { data, isFetching, refetch } = useListAffiliateCampaigns();

  const openCreateModal = () => {
    NiceModal.show(AffiliateCampaignCreateModal, {
      onSuccess: () => refetch(),
    });
  };

  const openDetailModal = (campaignCode: string) => {
    NiceModal.show(AffiliateCampaignDetailModal, { campaignCode });
  };

  const openEditModal = (record: CampaignDetail) => {
    NiceModal.show(AffiliateCampaignEditModal, {
      campaign: record,
      onSuccess: () => refetch(),
    });
  };

  const columns: TableColumnsType<CampaignDetail> = [
    {
      title: "รหัส Campaign",
      dataIndex: "campaignCode",
      key: "campaignCode",
      width: 160,
      sorter: (a, b) => a.campaignCode.localeCompare(b.campaignCode),
    },
    {
      title: "ชื่อ",
      dataIndex: "campaignName",
      key: "campaignName",
      sorter: (a, b) => a.campaignName.localeCompare(b.campaignName),
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (value: string) => <Tag color={statusColor[value] || "default"}>{value}</Tag>,
      filters: Object.values(CampaignStatus).map((s) => ({ text: s, value: s })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "ประเภท",
      dataIndex: "type",
      key: "type",
      width: 130,
      render: (value?: string | null) =>
        value ? <Tag color={typeColor[value] || "default"}>{value}</Tag> : "-",
      filters: Object.values(CampaignType).map((t) => ({ text: t, value: t })),
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "วันเริ่ม",
      dataIndex: "startDate",
      key: "startDate",
      width: 150,
      render: formatDateTime,
      sorter: (a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)),
    },
    {
      title: "วันสิ้นสุด",
      dataIndex: "endDate",
      key: "endDate",
      width: 150,
      render: formatDateTime,
      sorter: (a, b) => dayjs(a.endDate).diff(dayjs(b.endDate)),
    },
    {
      title: "วงเงิน Commission",
      dataIndex: "commissionLimit",
      key: "commissionLimit",
      width: 150,
      align: "right",
      render: (v?: number | null) => (v == null ? "-" : v.toLocaleString()),
    },
    {
      title: "",
      key: "action",
      width: 130,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <ViewMoreButton size="small" onClick={() => openDetailModal(record.campaignCode)} />
          {canEditCampaign && (
            <EditButton
              size="small"
              disabled={record.status === CampaignStatus.COMPLETED}
              onClick={() => openEditModal(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <CampaignDashboard />

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Affiliate Campaign ({data?.total ?? 0})
          </Title>
        </Col>
        <Col>
          {canCreateCampaign && (
            <AddButton onClick={openCreateModal}>สร้าง Campaign</AddButton>
          )}
        </Col>
      </Row>

      <Table
        rowKey="uid"
        loading={isFetching}
        columns={columns}
        dataSource={data?.campaigns || []}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: 1300 }}
      />
    </div>
  );
};

export default AffiliateCampaign;
